import { StatusCodes } from "http-status-codes";

import { createQueryKeys } from "@lukemorales/query-key-factory";

import type { CoreApiClient } from "../client";
import type { Floor, GetFloorStateDatesResponseBody, GetFloorStateResponseBody } from "../types/floor.type";

export const createFloorQueries = (api: CoreApiClient) => {
  const { client, request } = api;

  const list = async (buildingId: string): Promise<Floor[]> => {
    const { data } = await request("get", "/buildings/{buildingId}/floors", { params: { path: { buildingId } } });
    return data;
  };

  /** `null` when the floor has no plan yet — supplying a placeholder is the app's call, not the contract's. */
  const plan = async (floorId: string): Promise<File | null> => {
    const { data, error, response } = await client.GET("/floors/{floorId}/plan", {
      params: { path: { floorId } },
      parseAs: "blob",
    });
    if (response.status === StatusCodes.NOT_FOUND) return null;
    if (error) throw new Error(error.message);
    return new File([data], floorId, { type: data.type });
  };

  /** Live/historical snapshot. No `date` → live valve state on this floor; a `date` (ISO) → snapshot at that instant. */
  const state = async (floorId: string, date?: string): Promise<GetFloorStateResponseBody> => {
    const { data } = await request("get", "/floors/{floorId}/state", {
      params: { path: { floorId }, query: date ? { date } : undefined },
    });
    return data;
  };

  /** Distinct instants with a valve temperature sample on this floor — the history slider's real data points. */
  const stateDates = async (
    floorId: string,
    startDate: string,
    endDate: string,
  ): Promise<GetFloorStateDatesResponseBody> => {
    const { data } = await request("get", "/floors/{floorId}/state/dates", {
      params: { path: { floorId }, query: { startDate, endDate } },
    });
    return data;
  };

  return {
    list,
    plan,
    state,
    stateDates,
    queries: createQueryKeys("floor", {
      building: (buildingId: string) => ({ queryKey: [buildingId], queryFn: () => list(buildingId) }),
      plan: (floorId: string) => ({ queryKey: [floorId], queryFn: () => plan(floorId) }),
      state: (floorId: string, date?: string) => ({
        queryKey: [floorId, "state", date ?? "live"],
        queryFn: () => state(floorId, date),
      }),
      stateDates: (floorId: string, startDate: string, endDate: string) => ({
        queryKey: [floorId, "state-dates", startDate, endDate],
        queryFn: () => stateDates(floorId, startDate, endDate),
      }),
    }),
  };
};
